/**
 * Grok sidebar identity for workflow node bots, keyed by agent_id.
 *
 * The seven rows match
 * supabase/migrations/20261105060000_board_bot_identities.sql.
 * Tag is null on every row: the CEO list did not give a separate sidebar tag.
 * Color and shape stay null when they were not specified. Do not fill them in.
 *
 * Sidebar bots that still have no agent_id (and therefore no row) are listed
 * in that migration. 打工牛马 has no dedicated post and must stay unmapped.
 *
 * `store === null` means the identity table could not be read, so the known
 * catalog is used. An array, including an empty one, is the table result:
 * an agent missing from it is unmapped and must not pick up a catalog color.
 */

export const BOT_AVATAR_COLORS = ["orange", "violet", "brown"] as const;
export type BotAvatarColor = (typeof BOT_AVATAR_COLORS)[number];

export const BOT_AVATAR_SHAPES = [
  "cylinder",
  "crystal",
  "leaf",
  "gem",
  "pebble",
] as const;
export type BotAvatarShape = (typeof BOT_AVATAR_SHAPES)[number];

export type BotIdentity = {
  agent_id: string;
  display_name: string;
  tag: string | null;
  avatar_color: BotAvatarColor | null;
  avatar_shape: BotAvatarShape | null;
};

export type BotIdentityRow = {
  agent_id?: string | null;
  display_name?: string | null;
  tag?: string | null;
  avatar_color?: string | null;
  avatar_shape?: string | null;
};

const COLOR_SET = new Set<string>(BOT_AVATAR_COLORS);
const SHAPE_SET = new Set<string>(BOT_AVATAR_SHAPES);

export function asBotAvatarColor(
  value: string | null | undefined,
): BotAvatarColor | null {
  return value && COLOR_SET.has(value) ? (value as BotAvatarColor) : null;
}

export function asBotAvatarShape(
  value: string | null | undefined,
): BotAvatarShape | null {
  return value && SHAPE_SET.has(value) ? (value as BotAvatarShape) : null;
}

/** Named sidebar hues. The hex is this board's paint for that name, not a sampled pixel. */
export const BOT_AVATAR_FILL: Record<BotAvatarColor, string> = {
  orange: "#E36A1E",
  violet: "#6D4BD6",
  brown: "#8A5A3A",
};

export const KNOWN_BOT_IDENTITIES: readonly BotIdentity[] = [
  {
    agent_id: "f0a68444-1c3c-4373-8f6c-7b512439da3f",
    display_name: "观心微语·选题",
    tag: null,
    avatar_color: "orange",
    avatar_shape: null,
  },
  {
    agent_id: "6d6fa75c-63ef-48b3-a239-ad6ed78cf3b7",
    display_name: "观心微语·整片",
    tag: null,
    avatar_color: "orange",
    avatar_shape: "cylinder",
  },
  {
    agent_id: "4294154e-5175-4fa4-8c8e-2dfc6c9790de",
    display_name: "观心微语·口播审",
    tag: null,
    avatar_color: "violet",
    avatar_shape: "leaf",
  },
  {
    agent_id: "be762a93-2b60-41a8-a779-f0fdf2acaa1e",
    display_name: "观心微语·密度审",
    tag: null,
    avatar_color: "violet",
    avatar_shape: "gem",
  },
  {
    agent_id: "2564021c-a781-44b6-a9fb-08db5265b281",
    display_name: "观心微语·闸门审",
    tag: null,
    avatar_color: "violet",
    avatar_shape: "crystal",
  },
  {
    agent_id: "62333102-58c8-4251-b45e-3e8848b9b639",
    display_name: "观心微语·发布包",
    tag: null,
    avatar_color: "orange",
    avatar_shape: null,
  },
  {
    agent_id: "d9815ba8-5c5d-4bd4-b882-35ce89fd139a",
    display_name: "个人IP·Master",
    tag: null,
    avatar_color: null,
    avatar_shape: null,
  },
];

export function asBotIdentity(row: BotIdentityRow): BotIdentity | null {
  const agentId = row.agent_id?.trim();
  const displayName = row.display_name?.trim();
  if (!agentId || !displayName) return null;
  return {
    agent_id: agentId,
    display_name: displayName,
    tag: row.tag?.trim() || null,
    avatar_color: asBotAvatarColor(row.avatar_color),
    avatar_shape: asBotAvatarShape(row.avatar_shape),
  };
}

export function lookupBotIdentity(
  agentId: string,
  store: readonly BotIdentity[] | null,
): BotIdentity | null {
  const rows = store ?? KNOWN_BOT_IDENTITIES;
  return rows.find((row) => row.agent_id === agentId) ?? null;
}

export function botAvatarLabel(
  name: string,
  color: BotAvatarColor,
  shape: BotAvatarShape | null,
) {
  return shape
    ? `${name} 头像 ${color} ${shape}`
    : `${name} 头像 ${color} 形状未登记`;
}
